import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownProps {
  /** Raw markdown string to render. */
  children: string;
  className?: string;
}

// Element → Tailwind styling map. Built to match the app's secondary-*/
// primary-* / info-* design tokens. Everything wraps so long words and URLs
// never overflow the (narrow) chat drawer.
//
// SECURITY: react-markdown does NOT render raw HTML by default — we
// deliberately do NOT add `rehype-raw` (or any HTML-allowing plugin) because
// the markdown here is untrusted LLM output. Keeping the default behaviour
// means an injected `<img onerror=...>` is escaped as text rather than
// mounted as a live DOM node.
const components: Components = {
  p: ({ children }) => (
    <p className="my-2 break-words first:mt-0 last:mb-0">{children}</p>
  ),
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="my-2 list-disc space-y-1 pl-5 break-words">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-2 list-decimal space-y-1 pl-5 break-words">{children}</ol>
  ),
  li: ({ children }) => <li className="break-words">{children}</li>,
  h1: ({ children }) => (
    <h1 className="mb-2 mt-3 text-lg font-semibold first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-3 text-base font-semibold first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</h3>
  ),
  code: ({ className: codeClassName, children, ...props }) => {
    // react-markdown emits inline code as <code> without a language- class and
    // fenced blocks as <code class="language-xxx"> nested in <pre>. We only
    // style the inline variant here; fenced blocks inherit the <pre> styling
    // and keep the <code> transparent.
    const isBlock = /language-/.test(codeClassName ?? '');
    if (isBlock) {
      return (
        <code className={codeClassName} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        className="rounded bg-secondary-100 px-1 py-0.5 font-mono text-[0.85em] text-secondary-900"
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-2 overflow-x-auto whitespace-pre rounded-md bg-secondary-100 p-3 font-mono text-xs text-secondary-900">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse border border-secondary-300 text-left text-xs">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-secondary-100">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="border border-secondary-300 px-2 py-1 font-semibold">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-secondary-300 px-2 py-1 align-top">
      {children}
    </td>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-4 border-secondary-300 pl-3 text-secondary-600 italic">
      {children}
    </blockquote>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="break-words text-info-700 underline"
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-3 border-secondary-200" />,
};

/**
 * Renders an (untrusted) markdown string with GitHub-flavoured markdown
 * support (tables, strikethrough, task lists, autolinks). Raw HTML is NOT
 * rendered — see the security note on `components` above.
 */
export function Markdown({ children, className }: Readonly<MarkdownProps>) {
  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
